import {
  CopyReferencesIngestionStatus,
  type CopyReferencesIngestionRunsQuery,
  type CopyReferencesReviewsByInteractionQuery,
} from "src/graphql/types";

type IngestionRunRow =
  CopyReferencesIngestionRunsQuery["copyReferencesIngestionRuns"][number];

export type IngestionRun = IngestionRunRow & {
  reviewId: string | null;
  reviewPending: boolean;
};

type ReviewStatus = { reviewId: string; reviewPending: boolean };
export type ReviewStatusByInteraction = Map<string, ReviewStatus>;

const COMPLETED_WITHOUT_REVIEW_REASON =
  "The ingestion agent completed without producing a review.";

// Latest copy-references review per interaction. The query orders newest-first,
// so first-wins keeps the latest (one review per interaction).
export const reviewStatusByInteraction = (
  reviews: CopyReferencesReviewsByInteractionQuery["agentic_settings_entity_reviews"],
): ReviewStatusByInteraction => {
  const map: ReviewStatusByInteraction = new Map();
  for (const review of reviews) {
    if (map.has(review.llm_chat_interaction_id)) continue;
    map.set(review.llm_chat_interaction_id, {
      reviewId: review.id,
      reviewPending: (review.pending.aggregate?.count ?? 0) > 0,
    });
  }
  return map;
};

// `reviewsResolved` guards that downgrade: the review row lives in a separate
// query that is written when the run settles, so a just-completed run can look
// reviewless simply because the reviews query hasn't (re)fetched yet. Downgrading
// then would flash a false "failed". Callers pass `false` while the reviews query
// is loading/refetching so a genuinely reviewless run is only marked failed once
// its review status is authoritative.
export const withReviewStatus = (
  runs: IngestionRunRow[],
  reviewStatus: ReviewStatusByInteraction,
  reviewsResolved = true,
): IngestionRun[] =>
  runs.map((run) => {
    const review = run.interactionId
      ? reviewStatus.get(run.interactionId)
      : undefined;
    const hasReview = review != null;
    const completedWithoutReview =
      reviewsResolved &&
      run.status === CopyReferencesIngestionStatus.Complete &&
      !hasReview;
    return {
      ...run,
      status: completedWithoutReview
        ? CopyReferencesIngestionStatus.Failed
        : run.status,
      failureReason: completedWithoutReview
        ? COMPLETED_WITHOUT_REVIEW_REASON
        : run.failureReason,
      reviewId: review?.reviewId ?? null,
      reviewPending: review?.reviewPending ?? false,
    };
  });

export const isRunPending = (run: Pick<IngestionRun, "status">): boolean =>
  run.status === CopyReferencesIngestionStatus.Pending;

export const isRunCancellable = (
  run: Pick<IngestionRun, "status" | "interactionId">,
): boolean => isRunPending(run) && run.interactionId != null;

export const isRunActionable = (
  run: Pick<IngestionRun, "status" | "reviewId" | "reviewPending">,
): boolean =>
  isRunPending(run) ||
  (Boolean(run.reviewId) && run.reviewPending) ||
  run.status === CopyReferencesIngestionStatus.Failed ||
  run.status === CopyReferencesIngestionStatus.Canceled;

export const runFailureLabel = (
  run: Pick<IngestionRun, "status" | "failureReason">,
): string => {
  const reason = run.failureReason?.trim();
  if (reason) return reason;
  return run.status === CopyReferencesIngestionStatus.Canceled
    ? "Ingestion was canceled before it finished."
    : "Ingestion failed before producing references.";
};
